import { API_STATUS } from "@/constants";
import { ApiError, ApiResponse } from "@/types";

type FetcherOptions = RequestInit & {
  baseUrl?: string;
  simulateDelay?: boolean; // Tùy chọn để mô phỏng delay
  delayMs?: number; // Thời gian delay (mặc định 5000ms)
  skipTokenRefresh?: boolean; // Skip token refresh for this request
};

// Token refresh state management
let isRefreshing = false;
let refreshPromise: Promise<boolean> | null = null;

/**
 * Refresh token function
 */
async function refreshToken(): Promise<boolean> {
  try {
    // Use direct fetch to avoid circular dependency
    const response = await fetch("/api/oauth/refresh-token", {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (response.ok) {
      const data = await response.json();
      if (data.status === API_STATUS.OK) {
        return true;
      }
    }
    return false;
  } catch (error) {
    console.error("❌ Token refresh error:", error);
    return false;
  }
}

/**
 * Handle token refresh with singleton pattern
 */
async function handleTokenRefresh(): Promise<boolean> {
  if (isRefreshing && refreshPromise) {
    // If already refreshing, wait for the existing promise
    return refreshPromise;
  }

  isRefreshing = true;
  refreshPromise = refreshToken();

  try {
    const success = await refreshPromise;
    return success;
  } finally {
    isRefreshing = false;
    refreshPromise = null;
  }
}

/**
 * Hàm helper để delay (chờ) một khoảng thời gian
 */
function delay(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

/**
 * A wrapper around fetch that handles common functionality
 * - Automatically parses JSON responses
 * - Handles errors consistently
 * - Supports absolute and relative URLs
 * - Handles 401 errors with automatic token refresh
 */
export async function fetcher<T = unknown>(url: string, options: FetcherOptions = {}): Promise<T> {
  const {
    simulateDelay = false,
    delayMs = 5000,
    skipTokenRefresh = false,
    ...fetchOptions
  } = options;

  // Set default headers if not provided
  const headers: Record<string, string> = {
    ...(!(fetchOptions.body instanceof FormData) && { "Content-Type": "application/json" }),
    ...(options.headers as Record<string, string>),
  };

  // Internal fetch function
  const performFetch = async (): Promise<T> => {
    // Mô phỏng delay nếu được yêu cầu
    if (simulateDelay) {
      await delay(delayMs);
    }

    const response = await fetch(url, {
      ...fetchOptions,
      headers,
      credentials: "include",
    });

    // Parse the JSON response
    const data: ApiResponse<unknown> = await response.json();

    // Handle API error responses
    if (!response.ok) {
      throw new ApiError(
        response.status,
        data?.message || response.statusText,
        data.error_code,
        data
      );
    }

    return data as T;
  };

  try {
    return await performFetch();
  } catch (error) {
    // Handle 401 Unauthorized errors
    if (
      error instanceof ApiError &&
      error.status === 401 &&
      !skipTokenRefresh &&
      !url.includes("/auth/refresh") // Avoid infinite loop
    ) {
      try {
        const refreshSuccess = await handleTokenRefresh();
        if (refreshSuccess) {
          // Retry the original request with the new token
          return await performFetch();
        } else {
          throw error;
        }
      } catch (refreshError) {
        console.error("❌ Token refresh process failed:", refreshError);
        throw error;
      }
    }

    // For all other errors, just throw them
    throw error;
  }
}

// Public fetcher - no authentication required
export async function publicFetcher<T = unknown>(
  url: string,
  options: FetcherOptions = {}
): Promise<T> {
  const { simulateDelay = false, delayMs = 5000, ...fetchOptions } = options;

  // Set default headers if not provided
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...(options.headers as Record<string, string>),
  };

  // Internal fetch function
  const performFetch = async (): Promise<T> => {
    // Simulate delay if requested
    if (simulateDelay) {
      await delay(delayMs);
    }

    const response = await fetch(url, {
      ...fetchOptions,
      headers,
      // No credentials for public endpoints
    });

    // Parse the JSON response
    const data = await response.json();

    // Handle API error responses
    if (!response.ok) {
      throw new ApiError(
        response.status,
        data?.message || response.statusText,
        data.error_code,
        data
      );
    }
    return data as T;
  };

  try {
    return await performFetch();
  } catch (error) {
    // console.error('❌ Public Fetcher error:', error);
    throw error;
  }
}
